// askSmart-ui/src/pages/TOSP/seedTaxRolls.ts
//
// The rolls as the Town of The Blue Mountains bills them, transcribed from the
// 2026 Interim Tax Bill for customer BYDEM0002 — 152 parcels across five condo
// plans and two land parts.
//
// This exists so the register doesn't have to be keyed in by hand once. It is a
// ONE-TIME STARTING POINT, not an import feature: new rolls (a new store, a
// store split in two) are added on the Rolls tab from here on.
//
// Identity only. No levies, no instalments — those arrive with the real 2026
// ingest, and they belong to a tax year rather than to the roll.

import { newId, TaxRollAllocation, TaxRollRecord } from "./types";

/** A roll starts whole and unassigned: one unlabelled part, no unit. */
const wholeAndUnassigned = (): TaxRollAllocation[] => [
  {
    id: newId("alloc"),
    fromYear: 0,
    parts: [{ id: newId("part"), label: "", assignedTo: null }],
  },
];

/** [roll number's middle five digits, level, unit, address override]. */
type Seed = [string, string, string, string?];

interface SeedGroup {
  /** Condo plan number, digits only. */
  plan: string;
  /** Used by every roll in the group that doesn't override it. */
  address: string;
  /** Appended to the legal description, e.g. "LKR" for a locker. */
  suffix?: string;
  rolls: Seed[];
}

// Roll numbers all share this shape, so only the middle differs.
const rollNumber = (middle: string): string => `000-020-${middle}-0000`;

const JOZO = (n: number) => `${n} - JOZO WEIDER BLVD`;

const GROUPS: SeedGroup[] = [
  {
    plan: "61",
    address: JOZO(170),
    rolls: [
      ["00440", "1", "1"],
      ["00441", "1", "2"],
      ["00442", "1", "3"],
      ["00443", "1", "4"],
      ["00444", "1", "5"],
      ["00445", "1", "6"],
      ["00446", "1", "7"],
      ["00447", "1", "8"],
      ["00448", "1", "9"],
      ["00449", "1", "10"],
      ["00450", "1", "11"],
      ["00451", "1", "12"],
      ["00452", "1", "13"],
      ["00453", "1", "14"],
      ["00454", "1", "15"],
      ["00455", "1", "16"],
      ["00456", "1", "17"],
      ["00457", "1", "18"],
      ["00458", "1", "19"],
      ["00459", "1", "20"],
      ["00460", "1", "21"],
      ["00461", "1", "22"],
      ["00462", "1", "23"],
      ["00463", "1", "24"],
      ["00464", "1", "25"],
      ["00465", "1", "26"],
      ["00466", "1", "27"],
      ["00467", "1", "28"],
      ["00468", "1", "29"],
      ["00469", "1", "30"],
      ["00470", "1", "31"],
      ["00471", "1", "32"],
      ["00472", "1", "33"],
      ["00473", "1", "34", "20, 170 - JOZO WEIDER BLVD"],
      ["00474", "1", "35"],
      ["00475", "1", "36"],
      ["00476", "1", "37"],
      ["00477", "1", "38"],
      ["00478", "1", "39"],
      ["00479", "1", "40"],
      ["00480", "1", "41"],
      ["00481", "1", "42"],
      ["00482", "1", "43"],
      ["00483", "1", "44"],
      ["00484", "1", "45"],
      ["00485", "1", "46"],
      ["00486", "1", "47"],
      ["00487", "1", "48"],
      ["00488", "1", "49"],
      ["00489", "1", "50"],
      ["00490", "1", "51"],
      ["00491", "1", "52"],
      ["00492", "1", "53"],
      // 54–56 are billed out of sequence, after 66 — the roll number order and
      // the unit order genuinely don't agree.
      ["00502", "1", "54"],
      ["00503", "1", "55"],
      ["00504", "1", "56"],
      ["00493", "1", "57"],
      ["00494", "1", "58"],
      ["00495", "1", "59"],
      ["00496", "1", "60", JOZO(174)],
      ["00514", "1", "61", ""],
      ["00497", "1", "62", JOZO(172)],
      ["00498", "1", "63", JOZO(172)],
      ["00499", "1", "64", JOZO(166)],
      ["00500", "1", "65", JOZO(162)],
      ["00501", "1", "66", JOZO(162)],
      ["00507", "A", "1"],
      ["00508", "A", "2"],
      ["00509", "A", "3"],
      ["00510", "A", "4"],
      ["00511", "A", "5"],
      ["00513", "B", "1"],
      ["00512", "B", "2"],
    ],
  },
  {
    plan: "66",
    address: "220 - GORD CANNING DR",
    rolls: [
      ["00541", "A", "1"],
      ["00542", "1", "1"],
    ],
  },
  {
    plan: "47",
    address: JOZO(156),
    rolls: [
      ["16701", "1", "1"],
      ["16702", "1", "2"],
      ["16703", "1", "3"],
      ["16704", "1", "4"],
      ["16705", "1", "5"],
      ["16706", "1", "6"],
      ["16707", "1", "7"],
      ["16708", "1", "8"],
      ["16709", "1", "9"],
      ["16710", "1", "10"],
      ["16711", "1", "11"],
      ["16712", "1", "12"],
      ["16713", "1", "13"],
      ["16714", "1", "14"],
      ["16715", "1", "15"],
      ["16716", "1", "16"],
      ["16717", "1", "17"],
      ["16718", "1", "18"],
      ["16719", "1", "19"],
      ["16720", "1", "20"],
      ["16721", "1", "21"],
      ["16722", "1", "22"],
      ["16723", "1", "23"],
      ["16724", "1", "24"],
      ["16725", "1", "25"],
      ["16726", "1", "26"],
      ["16727", "1", "27"],
      ["16728", "1", "28"],
      ["16729", "1", "29"],
      ["16730", "1", "30"],
      ["16731", "A", "1"],
      ["16732", "A", "2"],
      ["16733", "A", "3"],
      ["16734", "A", "4"],
      ["16735", "A", "5"],
      ["16736", "A", "6"],
      ["16737", "B", "1"],
      ["16738", "B", "2"],
      ["16739", "B", "3"],
      ["16740", "B", "4"],
    ],
  },
  {
    plan: "96",
    address: JOZO(190),
    rolls: [
      ["16825", "1", "1"],
      ["16826", "1", "2"],
      ["16827", "1", "3"],
      ["16828", "1", "4"],
      ["16829", "1", "5"],
      ["16830", "1", "6"],
      ["16831", "1", "7"],
      ["16832", "1", "8"],
      ["16833", "1", "9"],
      ["16834", "1", "10"],
      ["16835", "1", "11"],
      ["16836", "1", "12"],
      ["16837", "1", "13"],
      // 14 and 15 aren't on this bill.
      ["16840", "1", "16"],
      ["16841", "1", "17"],
      ["16842", "1", "18"],
      ["16843", "1", "19"],
      ["16844", "1", "20"],
      ["16845", "1", "21"],
    ],
  },
  {
    // The lockers are billed as their own parcels — $84 to $350 apiece.
    plan: "96",
    address: JOZO(190),
    suffix: "LKR",
    rolls: [
      ["16846", "A", "1"],
      ["16849", "B", "1"],
      ["16850", "B", "2"],
    ],
  },
  {
    plan: "51",
    address: JOZO(152),
    rolls: [
      ["17001", "1", "1"],
      ["17003", "1", "2"],
      ["17005", "1", "3"],
      ["17007", "1", "4"],
      ["17009", "1", "5"],
      ["17011", "1", "6"],
      ["17013", "1", "7"],
      ["17015", "1", "8"],
      ["17017", "1", "9"],
      ["17019", "1", "10"],
      ["17021", "1", "11"],
      ["17023", "1", "12"],
      ["17025", "1", "13"],
    ],
  },
];

/** The two land parts at 180 Jozo Weider — no condo plan, no level, no unit. */
const LAND: { middle: string; legalDescription: string; address: string }[] = [
  {
    middle: "16860",
    legalDescription: "PLAN 1065 PT LOTS 6 AND 10 RP 16R10889 PART 1",
    address: "R8, 180 - JOZO WEIDER BLVD",
  },
  {
    middle: "16861",
    legalDescription: "PLAN 1065 PT LOTS 6 AND 10 RP 16R10889 PART 2",
    address: "",
  },
];

/** The bill these came off, shown in the register's Last billed column. */
export const SEED_BILL_LABEL = "2026 Interim";

/** Every seeded roll, unassigned and ready to be attached to a unit. */
export function seedTaxRolls(): TaxRollRecord[] {
  const fromGroups = GROUPS.flatMap((group) =>
    group.rolls.map(([middle, level, unit, address]) => ({
      id: newId("roll"),
      rollNumber: rollNumber(middle),
      planNumber: group.plan,
      level,
      unit,
      legalDescription: `GSCP ${group.plan} LEVEL ${level} UNIT ${unit}${
        group.suffix ? ` ${group.suffix}` : ""
      }`,
      municipalAddress: address ?? group.address,
      allocations: wholeAndUnassigned(),
      lastBilled: SEED_BILL_LABEL,
      // Identity only — the amounts arrive by ingesting the bill, and the
      // budgets when a year is opened.
      levies: [],
      budgets: [],
    })),
  );

  const fromLand = LAND.map((parcel) => ({
    id: newId("roll"),
    rollNumber: rollNumber(parcel.middle),
    planNumber: "",
    level: "",
    unit: "",
    legalDescription: parcel.legalDescription,
    municipalAddress: parcel.address,
    allocations: wholeAndUnassigned(),
    lastBilled: SEED_BILL_LABEL,
    levies: [],
    budgets: [],
  }));

  return [...fromGroups, ...fromLand];
}

/**
 * Pre-assigns rolls whose number was already keyed on a unit, back when the
 * Unit form asked for one.
 *
 * A unit that already carried 000-020-00440-0000 comes back assigned without
 * anyone touching it, so only the rolls never captured need picking. Rolls
 * already assigned are left alone.
 */
export function seedAssignmentsFromUnits(
  rolls: TaxRollRecord[],
  units: { id: string; taxRoll?: { rollNumber?: string } }[],
): TaxRollRecord[] {
  const unitByRollNumber = new Map<string, string>();
  for (const unit of units) {
    const number = unit.taxRoll?.rollNumber?.trim();
    // First unit to claim a number wins; a duplicate is a data error worth
    // seeing in the register rather than resolving silently here.
    if (number && !unitByRollNumber.has(number)) unitByRollNumber.set(number, unit.id);
  }

  return rolls.flatMap((roll) => {
    const allocation = roll.allocations[roll.allocations.length - 1];
    // Only a whole, unassigned roll is seeded — a divided one has already been
    // worked on by hand and mustn't be overwritten.
    if (allocation.parts.length !== 1 || allocation.parts[0].assignedTo) return [];
    const unitId = unitByRollNumber.get(roll.rollNumber);
    if (!unitId) return [];
    return [
      {
        ...roll,
        allocations: [
          ...roll.allocations.slice(0, -1),
          {
            ...allocation,
            parts: [{ ...allocation.parts[0], assignedTo: { type: "unit" as const, id: unitId } }],
          },
        ],
      },
    ];
  });
}
